export type CommentMutationLike = {
    action: 'created' | 'updated' | 'deleted';
    comment: any;
};

const toNumber = (value: unknown) => Number(value ?? 0);

const commentUserId = (comment: any) => toNumber(comment?.user?.id ?? comment?.id_user);
const parentCommentId = (comment: any) => toNumber(comment?.id_comment);
const commentText = (comment: any) => String(comment?.textFormatted ?? comment?.text ?? '').trim();
const commentGif = (comment: any) => String(comment?.gif_url ?? '');

export const normalizeEchoComment = (comment: any) => ({
    ...comment,
    textFormatted: comment?.textFormatted ?? comment?.text ?? '',
});

const isPendingVersionOf = (comment: any, incoming: any) => (
    comment?.idNotSend === true
    && commentUserId(comment) === commentUserId(incoming)
    && parentCommentId(comment) === parentCommentId(incoming)
    && commentText(comment) === commentText(incoming)
    && commentGif(comment) === commentGif(incoming)
);

const updateNested = (
    comments: any[],
    predicate: (comment: any) => boolean,
    update: (comment: any) => any,
): { comments: any[]; found: boolean } => {
    let found = false;

    const next = comments.map(comment => {
        if (!found && predicate(comment)) {
            found = true;
            return update(comment);
        }

        if (!found && Array.isArray(comment.responses)) {
            const nested = updateNested(comment.responses, predicate, update);
            if (nested.found) {
                found = true;
                return { ...comment, responses: nested.comments };
            }
        }

        return comment;
    });

    return { comments: next, found };
};

const removeNested = (comments: any[], commentId: number): any[] => comments
    .filter(comment => toNumber(comment?.id) !== commentId)
    .map(comment => Array.isArray(comment.responses)
        ? { ...comment, responses: removeNested(comment.responses, commentId) }
        : comment);

export const applyCommentMutation = (comments: any[], mutation: CommentMutationLike): any[] => {
    const incomingId = toNumber(mutation.comment?.id);
    if (!incomingId) return comments;

    if (mutation.action === 'deleted') {
        return removeNested(comments, incomingId);
    }

    const incoming = normalizeEchoComment(mutation.comment);
    const exactMatch = updateNested(
        comments,
        comment => toNumber(comment?.id) === incomingId,
        comment => ({ ...comment, ...incoming, idNotSend: comment?.idNotSend ? false : comment?.idNotSend }),
    );
    if (exactMatch.found) return exactMatch.comments;

    if (mutation.action === 'created') {
        const pendingMatch = updateNested(
            comments,
            comment => isPendingVersionOf(comment, incoming),
            comment => ({ ...comment, ...incoming, id: incomingId, idNotSend: false }),
        );
        if (pendingMatch.found) return pendingMatch.comments;
    }

    if (parentCommentId(incoming)) {
        return comments.map(comment => toNumber(comment?.id) === parentCommentId(incoming)
            ? {
                ...comment,
                total_responses: toNumber(comment.total_responses) + 1,
                responses: [incoming, ...(comment.responses ?? [])],
            }
            : comment);
    }

    return [incoming, ...comments];
};
